import { useCallback } from 'react';

import type { Meeting } from './meetingTypes.ts';

/**
 * Opens the embedded Loom recording for a meeting.
 *
 * ## Why this indirection exists
 * The real embedded player is `@atlassian/loom-preview-panel`, opened via its
 * `useLoomPreviewPanel().openLoomPreviewPanel({ url, expandUrl, ari })` hook.
 * That hook REQUIRES a `<PreviewPanelsProvider>` ancestor and a Relay
 * environment — neither of which the For You surface currently mounts.
 *
 * To keep the shell mock-safe and decoupled, this hook accepts an injectable
 * `openPanel` implementation:
 *
 * - In the shell / stories / tests: no implementation is passed, so it falls
 *   back to opening the Loom share URL in a new tab (safe, always works).
 * - In production wiring (follow-up): pass
 *   `openLoomPreviewPanel` from `useLoomPreviewPanel()` at a call site that is
 *   inside a `PreviewPanelsProvider`, e.g.
 *
 *   ```tsx
 *   const { openLoomPreviewPanel } = useLoomPreviewPanel();
 *   const openRecording = useMeetingPreview({
 *     openPanel: ({ url, expandUrl, ari }) => openLoomPreviewPanel({ url, expandUrl, ari }),
 *   });
 *   ```
 *
 * This is the single, clearly-marked seam for the real player integration.
 */

export type LoomPanelArgs = {
	url: string;
	expandUrl: string;
	ari: string;
};

export type UseMeetingPreviewOptions = {
	/** Real implementation, injected in production (see module doc). */
	openPanel?: (args: LoomPanelArgs) => void;
};

/** Derives the embed URL from a Loom share URL (share -> embed). */
const toEmbedUrl = (shareUrl: string): string => shareUrl.replace('/share/', '/embed/');

export const useMeetingPreview = (
	options: UseMeetingPreviewOptions = {},
): ((meeting: Meeting) => void) => {
	const { openPanel } = options;

	return useCallback(
		(meeting: Meeting) => {
			const args: LoomPanelArgs = {
				url: toEmbedUrl(meeting.videoUrl),
				expandUrl: meeting.videoUrl,
				ari: meeting.videoAri,
			};

			if (openPanel) {
				openPanel(args);
				return;
			}

			// Fallback: open the recording in a new tab. Used by the shell, stories,
			// and tests where no PreviewPanelsProvider is mounted.
			// Guard the specific API (per Confluence SSR rules), not the `window` global.
			if (typeof window.open === 'function') {
				window.open(meeting.videoUrl, '_blank', 'noopener,noreferrer');
			}
		},
		[openPanel],
	);
};
